import { useEffect, useRef } from 'react';
import L from 'leaflet';
import {
  getAreaTypeColor, EXCLUSION_TYPE_COLORS, isExclusionArea,
} from '../../SiteTakeoff/siteTakeoffConstants';

/**
 * Syncs an array of areas to Leaflet layers on the map.
 * Adds/updates/removes layers to match the current `areas` state.
 * Calls onAreaClick(areaId) when the user clicks a layer.
 *
 * Parent polygons with exclusions are rendered as donuts (outer ring + hole rings).
 * Exclusion areas are rendered with dashed borders and gray fill.
 */
export default function useAreaRendering({ mapRef, drawnItemsRef, areas, onAreaClick, visible = true, activeAreaId = null, draggableAreaId = null, onAreaMoved = null }) {
  const layerMapRef = useRef({}); // areaId -> leaflet layer
  const prevActiveRef = useRef(null); // track previous active for de-highlight
  const dragStateRef = useRef(null); // { areaId, startLatLng }

  // Highlight / de-highlight the active area
  useEffect(() => {
    const prevId = prevActiveRef.current;
    const map = mapRef.current;
    if (!map) return;

    // De-highlight previous
    if (prevId && prevId !== activeAreaId && layerMapRef.current[prevId]) {
      const layer = layerMapRef.current[prevId];
      const area = areas.find(a => (a._id || a.id) === prevId);
      if (area && layer.setStyle) {
        const isExc = isExclusionArea(area);
        const baseColor = isExc
          ? ((area.exclusionType && EXCLUSION_TYPE_COLORS[area.exclusionType]) || '#9E9E9E')
          : getAreaTypeColor(area.areaType);
        layer.setStyle({
          weight: 2,
          color: isExc ? '#333' : (area.strokeColor || '#000'),
          fillOpacity: isExc ? 0.45 : 0.3,
          ...(isExc ? {} : { fillColor: baseColor }),
        });
      }
      if (area && layer instanceof L.Marker) {
        const baseColor = getAreaTypeColor(area.areaType);
        layer.setIcon(L.divIcon({
          className: '',
          html: `<div style="width:18px;height:18px;border-radius:50%;background:${baseColor};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }));
      }
    }

    // Highlight current
    if (activeAreaId && layerMapRef.current[activeAreaId]) {
      const layer = layerMapRef.current[activeAreaId];
      const area = areas.find(a => (a._id || a.id) === activeAreaId);
      if (area && layer.setStyle) {
        layer.setStyle({
          weight: 4,
          color: '#1976d2',
          fillOpacity: 0.45,
        });
      }
      if (area && layer instanceof L.Marker) {
        const baseColor = getAreaTypeColor(area.areaType);
        layer.setIcon(L.divIcon({
          className: '',
          html: `<div style="width:22px;height:22px;border-radius:50%;background:${baseColor};border:3px solid #1976d2;box-shadow:0 0 8px rgba(25,118,210,0.6);"></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }));
      }
    }

    prevActiveRef.current = activeAreaId;
  }, [activeAreaId, areas, mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;

    const currentIds = new Set(areas.map(a => a._id || a.id));

    // Build a map of parentAreaId -> child exclusion areas for donut rendering
    const exclusionsByParent = {};
    areas.forEach(area => {
      if (isExclusionArea(area) && area.paths?.length) {
        const parentId = typeof area.parentAreaId === 'object' ? area.parentAreaId.toString() : area.parentAreaId;
        if (!exclusionsByParent[parentId]) exclusionsByParent[parentId] = [];
        exclusionsByParent[parentId].push(area);
      }
    });

    // Remove layers that no longer exist
    Object.keys(layerMapRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        const layer = layerMapRef.current[id];
        try { drawnItems.removeLayer(layer); } catch (_) {}
        delete layerMapRef.current[id];
      }
    });

    // Track which parent IDs need re-rendering due to exclusion changes
    // We re-render parents when their exclusion count/paths change
    const parentIdsWithExclusions = new Set(Object.keys(exclusionsByParent));

    // Add/update layers for current areas
    areas.forEach(area => {
      const areaId = area._id || area.id;
      if (!area.paths?.length) return;

      const isExclusion = isExclusionArea(area);
      const hasExclusions = !isExclusion && parentIdsWithExclusions.has(areaId);

      // For parents with exclusions, always re-render to pick up hole changes
      if (hasExclusions && layerMapRef.current[areaId]) {
        try { drawnItems.removeLayer(layerMapRef.current[areaId]); } catch (_) {}
        delete layerMapRef.current[areaId];
      }

      // Already rendered — skip
      if (layerMapRef.current[areaId]) return;

      const color = getAreaTypeColor(area.areaType);
      let layer;

      if (area.geometryType === 'point') {
        const p = area.paths[0];
        if (!p?.lat || !p?.lng) return;
        layer = L.marker([p.lat, p.lng], {
          draggable: true,   // Disabled at the handler level by default — enabled via useAreaEditing
          autoPan: true,
          icon: L.divIcon({
            className: '',
            html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          }),
        });
        // Disable drag initially — only useAreaEditing should enable it
        if (layer.dragging) {
          try { layer.dragging.disable(); } catch (_) {}
        }
      } else if (area.geometryType === 'ellipse') {
        const p = area.paths[0];
        if (!p?.lat || !p?.lng) return;
        const radiusMeters = (area.radiusX || 0) / 3.28084;
        layer = L.circle([p.lat, p.lng], {
          radius: radiusMeters,
          fillColor: color,
          fillOpacity: 0.25,
          color,
          weight: 2,
        });
      } else if (area.geometryType === 'polyline') {
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        layer = L.polyline(latlngs, { color, weight: 4, opacity: 0.8 });
      } else if (isExclusion) {
        // Exclusion area — dashed border, gray fill, higher opacity
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        const excColor = (area.exclusionType && EXCLUSION_TYPE_COLORS[area.exclusionType]) || '#9E9E9E';
        layer = L.polygon(latlngs, {
          fillColor: excColor,
          fillOpacity: 0.45,
          color: '#333',
          weight: 2,
          dashArray: '6, 4',
        });
      } else {
        // polygon (default) — render with holes if exclusions exist
        const outerRing = area.paths.map(p => [p.lat, p.lng]);

        if (hasExclusions) {
          const holeRings = exclusionsByParent[areaId].map(exc =>
            exc.paths.map(p => [p.lat, p.lng])
          );
          layer = L.polygon([outerRing, ...holeRings], {
            fillColor: color,
            fillOpacity: 0.3,
            color: area.strokeColor || '#000',
            weight: 2,
          });
        } else {
          layer = L.polygon(outerRing, {
            fillColor: color,
            fillOpacity: 0.3,
            color: area.strokeColor || '#000',
            weight: 2,
          });
        }
      }

      if (!layer) return;

      layer.on('click', () => {
        if (onAreaClick) onAreaClick(areaId);
      });

      drawnItems.addLayer(layer);
      layerMapRef.current[areaId] = layer;
    });
  }, [areas, mapRef, drawnItemsRef, onAreaClick]);

  // Show/hide the entire drawn-items layer group
  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;
    if (visible) {
      if (!map.hasLayer(drawnItems)) map.addLayer(drawnItems);
    } else {
      if (map.hasLayer(drawnItems)) map.removeLayer(drawnItems);
    }
  }, [visible, mapRef, drawnItemsRef]);

  // Make a polygon/polyline/circle draggable when draggableAreaId is set
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !draggableAreaId || !onAreaMoved) return;
    const layer = layerMapRef.current[draggableAreaId];
    if (!layer) return;

    // For markers, enable built-in dragging
    if (layer instanceof L.Marker) {
      if (layer.dragging) layer.dragging.enable();
      const onDragEnd = () => {
        const ll = layer.getLatLng();
        onAreaMoved(draggableAreaId, [{ lat: ll.lat, lng: ll.lng }]);
      };
      layer.on('dragend', onDragEnd);
      return () => {
        if (layer.dragging) layer.dragging.disable();
        layer.off('dragend', onDragEnd);
      };
    }

    // For polygons/polylines/circles — manual drag via map events
    let startLatLng = null;
    layer.setStyle({ cursor: 'grab' });
    // Add a CSS class for grab cursor on the layer's element
    const el = layer.getElement?.();
    if (el) el.style.cursor = 'grab';

    const onMouseDown = (e) => {
      L.DomEvent.stopPropagation(e);
      startLatLng = e.latlng;
      map.dragging.disable();
      if (el) el.style.cursor = 'grabbing';
      map.on('mousemove', onMouseMove);
      map.once('mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!startLatLng) return;
      const dLat = e.latlng.lat - startLatLng.lat;
      const dLng = e.latlng.lng - startLatLng.lng;
      startLatLng = e.latlng;

      if (layer instanceof L.Circle) {
        const c = layer.getLatLng();
        layer.setLatLng([c.lat + dLat, c.lng + dLng]);
      } else if (layer.getLatLngs) {
        const shift = (latlngs) => {
          if (Array.isArray(latlngs[0]) && !('lat' in latlngs[0])) {
            return latlngs.map(ring => shift(ring));
          }
          return latlngs.map(ll => L.latLng(ll.lat + dLat, ll.lng + dLng));
        };
        layer.setLatLngs(shift(layer.getLatLngs()));
      }
    };

    const onMouseUp = () => {
      map.off('mousemove', onMouseMove);
      map.dragging.enable();
      if (el) el.style.cursor = 'grab';

      // Extract final paths
      let newPaths;
      if (layer instanceof L.Circle) {
        const c = layer.getLatLng();
        newPaths = [{ lat: c.lat, lng: c.lng }];
      } else if (layer.getLatLngs) {
        const lls = layer.getLatLngs();
        const flat = Array.isArray(lls[0]) && !('lat' in lls[0]) ? lls[0] : lls;
        newPaths = flat.map(ll => ({ lat: ll.lat, lng: ll.lng }));
      }
      if (newPaths) onAreaMoved(draggableAreaId, newPaths);
    };

    layer.on('mousedown', onMouseDown);

    return () => {
      layer.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.dragging.enable();
      if (el) el.style.cursor = '';
    };
  }, [draggableAreaId, onAreaMoved, mapRef]);

  return { layerMapRef };
}
